// A trade document (src/lib/tradeDocs.js) shown at the width of its column,
// exactly as it prints. It renders in an iframe so the page's print styles
// stay apart from Farm OS's own; after the first load only the body is
// swapped, so typing doesn't reload the frame or flash the fonts.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { docHtml } from '../lib/tradeDocs';

const PX_PER_MM = 96 / 25.4;

export default function TradeDocPreview({ doc, onOverflow }) {
  const wrapRef = useRef(null);
  const frameRef = useRef(null);
  const latest = useRef(doc);
  const loadedCss = useRef(null);
  const ready = useRef(false);
  const [srcDoc, setSrcDoc] = useState(null);
  const [width, setWidth] = useState(0);
  const pageW = doc.widthMm * PX_PER_MM;
  const pageH = doc.heightMm * PX_PER_MM;
  const [height, setHeight] = useState(pageH);
  latest.current = doc;

  useLayoutEffect(() => {
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const measure = useCallback(() => {
    const d = frameRef.current?.contentDocument;
    if (!d?.body) return;
    setHeight(Math.max(pageH, d.documentElement.scrollHeight));
    const sheet = d.querySelector('.sheet');
    onOverflow?.(!!sheet && sheet.scrollHeight > sheet.clientHeight + 1);
  }, [pageH, onOverflow]);

  useEffect(() => {
    if (loadedCss.current !== doc.css) {
      loadedCss.current = doc.css;
      ready.current = false;
      setSrcDoc(docHtml(doc));
    } else if (ready.current) {
      frameRef.current.contentDocument.body.innerHTML = doc.body;
      measure();
    }
    // Otherwise the frame is still loading; onLoad puts the latest body in.
  }, [doc.css, doc.body]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLoad = () => {
    const d = frameRef.current?.contentDocument;
    if (!d?.body) return;
    ready.current = true;
    d.body.innerHTML = latest.current.body;
    measure();
    d.fonts?.ready.then(measure);
  };

  const scale = width ? width / pageW : 0;
  return (
    <div ref={wrapRef}>
      <div className="doc-preview" style={{ height: scale ? height * scale : pageH / 2 }}>
        {srcDoc && (
          <iframe
            ref={frameRef}
            title={doc.title}
            srcDoc={srcDoc}
            sandbox="allow-same-origin"
            scrolling="no"
            onLoad={onLoad}
            style={{ width: pageW, height, transform: `scale(${scale})` }}
          />
        )}
      </div>
      {height > pageH + 2 && <div className="doc-preview__note">This runs onto more than one page when printed.</div>}
    </div>
  );
}
